import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Button, CircularProgress } from "@mui/material";
import React, { useState } from "react";

import { formatTime } from "../../utils/generalFunctions";
import classes from "./NonverbalSounds.module.css";
import { HELD_BACK_TEXT, confidencePercent, dubStatus, labelOf, nonverbalOf } from "./nonverbal.js";

const STATUS = {
    in: { text: "In the dub", className: "StatusIn" },
    held: { text: "Needs your OK", className: "StatusHeld" },
    out: { text: "Left out", className: "StatusOut" },
};

const FILTERS = [
    ["all", "All"],
    ["in", "In the dub"],
    ["held", "Needs your OK"],
    ["out", "Left out"],
];

const SoundRow = ({ region, busy, onReview, onSeek }) => {
    const nonverbal = nonverbalOf(region);
    const status = dubStatus(region);
    const confidence = confidencePercent(nonverbal);
    return (
        <div
            className={`${classes.Row} ${status === "out" ? classes.RowOut : ""}`}
            role="row"
            data-testid="nonverbal-sound-row"
        >
            <div className={classes.Sound} role="cell">
                <span className={classes.Label}>{labelOf(nonverbal)}</span>
                {status === "held" ? (
                    <p className={classes.Note}>
                        {HELD_BACK_TEXT[nonverbal.heldBack] || "Left out until you add it"}
                    </p>
                ) : null}
            </div>
            <div role="cell">
                <button
                    type="button"
                    className={classes.Time}
                    onClick={() => onSeek?.(region.start)}
                    title="Play from here"
                >
                    <PlayArrowRoundedIcon fontSize="small" />
                    {formatTime(region.start)} – {formatTime(region.end)}
                </button>
            </div>
            <div className={classes.Confidence} role="cell">
                {confidence ? (
                    <>
                        <div className={classes.Meter}>
                            <div className={classes.MeterFill} style={{ width: confidence }} />
                        </div>
                        <span>{confidence}</span>
                    </>
                ) : null}
            </div>
            <div role="cell">
                <span className={`${classes.Status} ${classes[STATUS[status].className]}`}>
                    {STATUS[status].text}
                </span>
            </div>
            <div className={classes.Actions} role="cell">
                {status === "in" ? (
                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => onReview(region, "reject")}
                    >
                        Leave out
                    </Button>
                ) : null}
                {status === "held" ? (
                    <>
                        <Button
                            size="small"
                            color="inherit"
                            disabled={busy}
                            onClick={() => onReview(region, "reject")}
                        >
                            Dismiss
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={busy}
                            onClick={() => onReview(region, "accept")}
                        >
                            Add to dub
                        </Button>
                    </>
                ) : null}
                {status === "out" ? (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => onReview(region, "accept")}
                    >
                        Put back
                    </Button>
                ) : null}
            </div>
        </div>
    );
};

// Every sound non-verbal detection found, one row each. Sounds are in the dub
// unless rejected; ones held back (over original speech, say) wait for a
// user to add them.
const NonverbalSoundsSection = ({ regions, loading, busyId, onReview, onSeek }) => {
    const [filter, setFilter] = useState("all");
    const counts = { all: regions.length, in: 0, held: 0, out: 0 };
    regions.forEach((region) => {
        counts[dubStatus(region)] += 1;
    });
    const shown = filter === "all" ? regions : regions.filter((r) => dubStatus(r) === filter);

    return (
        <section className={classes.Section} data-testid="nonverbal-sounds-section">
            <div className={classes.Header}>
                <div>
                    <h3 className={classes.Title}>Non-verbal sounds</h3>
                    <p className={classes.Subtitle}>
                        Coughs, laughs, gasps and similar sounds from the original actors. They
                        play in the dub unless you leave them out.
                    </p>
                </div>
                <div className={classes.Filters} role="tablist">
                    {FILTERS.map(([key, text]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={filter === key}
                            className={`${classes.Filter} ${
                                filter === key ? classes.FilterSelected : ""
                            }`}
                            onClick={() => setFilter(key)}
                        >
                            {text} ({counts[key]})
                        </button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div className={classes.Empty}>
                    <CircularProgress size={20} />
                </div>
            ) : shown.length ? (
                <div className={classes.Rows} role="table" aria-label="Non-verbal sounds">
                    <div className={classes.HeadRow} role="row">
                        <span role="columnheader">Sound</span>
                        <span role="columnheader">Time</span>
                        <span role="columnheader">Confidence</span>
                        <span role="columnheader">Status</span>
                        <span role="columnheader" />
                    </div>
                    {shown.map((region) => (
                        <SoundRow
                            key={region.dbid}
                            region={region}
                            busy={busyId === region.dbid}
                            onReview={onReview}
                            onSeek={onSeek}
                        />
                    ))}
                </div>
            ) : (
                <div className={classes.Empty}>
                    {regions.length
                        ? "No sounds in this group."
                        : "No non-verbal sounds yet. Run detection from the Actions page."}
                </div>
            )}
        </section>
    );
};

export default NonverbalSoundsSection;

// The page's switch between its dialogue lines and this section.
export const WorkSectionSwitch = ({ value, onChange, soundCount }) => (
    <div className={classes.Switch} role="tablist" aria-label="Show">
        {[
            ["dialogue", "Dialogue", null],
            ["nonverbal", "Non-verbal sounds", soundCount],
        ].map(([key, text, count]) => (
            <button
                key={key}
                type="button"
                role="tab"
                aria-selected={value === key}
                className={`${classes.Filter} ${value === key ? classes.FilterSelected : ""}`}
                onClick={() => onChange(key)}
            >
                {text}
                {count ? <span className={classes.SwitchCount}>{count}</span> : null}
            </button>
        ))}
    </div>
);
